<template>
  <fade-transition>
    <aside class="chat" v-show="loaded">
      <SidebarHeader
        class="chat__header"
        :menuItems="[
        ... adminMenuItems,
          {
            icon: 'delete',
            text: 'Clear Chat',
            show: userLevel > 0,
            click: deleteAllChatMessagesHandler
          }
        ]"
      >
        <template #title>{{ currentChatTitle }}</template>
      </SidebarHeader>

      <div class="chat__messaging-container">
        <div class="chat__messages-loading" v-show="ifLoadingChat">
          <slide-in-transition-group v-if="ifLoadingChat" :total="skeletonMessagesNo" :reverse="true" direction="up">
            <div
              v-for="skeleton in skeletonMessagesNo"
              class="message-skeleton"
              :key="skeleton"
              :style="{ '--i': skeleton - 1 }"
            >
              <div class="message-skeleton__icon"></div>
              <div class="message-skeleton__name skeleton-global-shimmer"></div>
              <div class="message-skeleton__message skeleton-global-shimmer"></div>
            </div>
          </slide-in-transition-group>
        </div>

        <div
          class="chat__messages"
          :class="{ 'chat__messages--reply-active': currentReplyMessage }"
          ref="messages"
          @scroll="scrollForMessageHistory"
          v-show="chatMessages.length && !ifLoadingChat && loaded"
        >
          <div class="loading-icon-container" v-if="ifLoadingMoreChatMessages">
            <LoadingIcon size="tiny" />
          </div>
          <div class="chat__no-more-messages" v-else-if="noMoreMessages">No more messages!</div>

          <slide-in-transition-group
            class="chat__messages-list"
            v-if="!ifLoadingChat"
            :total="chatMessages.length"
            :reverse="true"
            direction="up"
          >
            <div
              class="chat__message-container"
              v-for="(message, index) in chatMessages"
              :key="message.id"
              :id="message.id"
              :style="{ '--i': index }"
            >
              <chat-message
                v-if="message.quotedMessage"
                :message="message.quotedMessage"
                :currentUsersData="currentUsersData"
                :quotedMessage="true"
              />
              <chat-message :message="message" :currentUsersData="currentUsersData" />
            </div>
          </slide-in-transition-group>
        </div>

        <fade-transition>
          <div class="chat__no-messages" v-if="!chatMessages.length && !ifLoadingChat && loaded">
            <img src="@/assets/chat-icons/no-chat-messages.png" alt="No chat messages" />
            <p>
              Type a message to get <br />
              a conversation going!
            </p>
          </div>
        </fade-transition>
        <div
          ref="chatMessageInputCont"
          class="chat__message-input"
          :class="{ 'reply-background': currentReplyMessage, notFocused: !inputFocused }"
        >
          <div class="emoji-container">
            <fade-transition :inDuration="400" :outDuration="50">
              <chat-emoji-picker
                v-show="showEmojiPicker"
                :closeEmojiPopup="closeEmojiPopup"
                :showEmojiPicker="showEmojiPicker"
                :selectEmoji="selectEmoji"
              />
            </fade-transition>
          </div>

          <div class="input-boxes" :class="{ 'emoji-picker-background': showEmojiPicker }">
            <div class="chat__message-reply" v-show="currentReplyMessage">
              <div class="chat__message-reply-options">
                <span class="close-reply" @click="clearCurrentReplyMessage"></span>
              </div>
              <chat-message-name-header v-if="currentReplyMessage" :message="currentReplyMessage" />
              <p class="message" v-html="currentReplyMessage ? currentReplyMessage.content : ''"></p>
            </div>

            <inputfield
              large
              :placeholder="inputFocused ? '' : currentReplyMessage ? 'Type a reply' : 'Type a message'"
              :customClasses="['bg-dark-black', 'text-white', 'text-bold']"
              v-model="messageInputValue"
              @keypress.enter="sendMessageHandler"
              @focus="inputFocused = true"
              @focusout="inputFocused = false"
              enterkeyhint="send"
              ref="chatMessageInput"
            >
              <template #image>
                <!-- emoji picker -->
                <img
                  class="text-white pick-emoji active"
                  src="@/assets/chat-icons/emoji.svg"
                  alt="emoji picker"
                  @click="toggleSelectEmojiPopup"
                />
                <!-- send message -->
                <img
                  v-if="messageInputValue"
                  class="text-white send-message active"
                  src="@/assets/chat-icons/send.svg"
                  alt="send message"
                  @click="sendMessageHandler"
                />
                <img v-else class="text-white send-message" src="@/assets/chat-icons/send.svg" alt="type to send message" />
              </template>
            </inputfield>
          </div>
        </div>
      </div>
    </aside>
  </fade-transition>
</template>

<script>
import Inputfield from '@/framework/inputfield/Inputfield.vue';
import ChatMessage from './ChatMessage.vue';
import ChatMessageNameHeader from './ChatMessageNameHeader.vue';
import ChatEmojiPicker from './ChatEmojiPicker.vue';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import SlideInTransitionGroup from '@/components/transitions/SlideInTransitionGroup.vue';
import LoadingIcon from '@/components/global/loading/LoadingIcon.vue';
import SidebarHeader from '../SidebarHeader.vue';
import CustomiseRoomModal from '@/components/modals/nav/CustomiseRoomModal.vue';
import RoomAdminSettingsMixin from "@/components/sidebar/RoomAdminSettingsMixin.js"

import { mapGetters, mapActions, mapMutations } from 'vuex';

export default {
  name: 'Chat',
  components: {
    Inputfield,
    ChatMessage,
    ChatMessageNameHeader,
    ChatEmojiPicker,
    FadeTransition,
    SlideInTransitionGroup,
    LoadingIcon,
    SidebarHeader
  },
  mixins: [
    RoomAdminSettingsMixin
  ],
  data: () => ({
    loaded: false,
    skeletonMessagesNo: 20,
    messageInputValue: '',
    inputFocused: true,
    showAdminOptions: false,
    editLocation: false,
    chatNameInput: '',
    scrolledToBottom: true,
    showEmojiPicker: false,
    emojiPicker: null,
    lastOpenedEmojiPicker: 0,
    chatBlocked: false
  }),
  computed: {
    ...mapGetters({
      ifLoadingChat: 'getIfLoadingChat',
      ifLoadingMoreChatMessages: 'getIfLoadingMoreChatMessages',
      chatMessages: 'getCurrentChatMessages',
      currentReplyMessage: 'getCurrentReplyMessage',
      currentUsersData: 'current_user/data',
      currentChatTitle: 'getCurrentChatTitle',
      scrollToBottomOfChat: 'getIfScrollToBottomOfChat',
      noMoreMessages: 'getNoMoreMessages',
      currentRoute: 'routing/routeName',
      data: 'getCurrentSpacePageData',
			user: 'current_user/data',
      userLevel: 'current_user/currentAreaPermissionNo'
    }),
    ...mapGetters(['getCurrentChatId', 'getCurrentAreaId', 'getCurrentAreaHubId']),
    isUserCreator: vm => vm.user.email === vm.data.hostEmail,
		roomLifetime: vm => vm.data.location?.locationLifestyle,
    isHub: vm => vm.data.location?.spaceType === 'hub',
		isHubOrLounge: vm => vm.isHub || vm.data.location?.spaceType === 'lounge',
  },
  watch: {
    async currentReplyMessage(val) {
      this.focusChatInput();

      // thought it'd be nice to scroll up and down by the height of the reply panel, so no messages are hidden when you hit the reply button
      // the conditionals below ensure the functionality works, and works with other scroll-related stuff
      if (!val && this.scrolledToBottom) return;
      if (val) await this.$nextTick();

      if (!this.$refs.messages) return;
      this.$refs.messages.scrollTop += 80 * (val ? 1 : -1);
    },
    scrollToBottomOfChat(val) {
      if (val) {
        if (this.scrolledToBottom) this.scrollToBottomOfMessages();
        this.setIfScrollToBottomOfChat(false);
      }
    },
    getCurrentChatId(newval, oldval) {
      if (newval === oldval && !this.chatBlocked) return;
      this.unload();
      this.load();
    },
    currentRoute(newval, oldval) {
      if (newval === oldval) return;
      this.unload();
      this.load();
    }
  },
  methods: {
    ...mapActions([
      'loadChatHandler',
      'clearCurrentReplyMessage',
      'deleteAllChatMessagesHandler',
      'sendReplyToChatMessageHandler',
      'sendChatMessageHandler',
      'clearChatMessages',
      'loadMoreChatMessagesHandler',
      'routing/routeName',
      'modal/open'
    ]),
    ...mapMutations(['setNewChatWaiting', 'setIfScrollToBottomOfChat']),
    focusChatInput() {
      this.$refs.chatMessageInput?.$el.querySelector('input')?.focus();
    },
    scrollToEndOfChatInput() {
      let inputEl = this.$refs.chatMessageInput?.$el.querySelector('input');
      if (inputEl) inputEl.scrollLeft = inputEl.scrollWidth;
    },
    async sendMessageHandler() {
      let content = this.messageInputValue.trim();

      if (!content) return;

      let replyTo = this.currentReplyMessage?.id;

      if (replyTo) {
        this.sendReplyToChatMessageHandler({ content, replyTo });
      } else {
        this.sendChatMessageHandler({ content });
      }

      this.messageInputValue = '';
      this.focusChatInput();
      this.scrollToBottomOfMessages();
    },
    async scrollToBottomOfMessages() {
      if (!this.$refs.messages) return;
      let messages = this.$refs.messages;
      if (messages.scrollHeight < messages.offsetHeight) return;
      setTimeout(() => {
        messages.scrollTop = messages.scrollHeight;
      }, 100);
    },
    scrollForMessageHistory(onLoad = false) {
      if (!this.$refs.messages) return;
      let { scrollTop, scrollHeight, offsetHeight } = this.$refs.messages;
      this.scrolledToBottom = scrollHeight - scrollTop === offsetHeight;

      if (scrollTop < 300 && !onLoad)
        this.loadMoreChatMessagesHandler({
          referenceDate: this.chatMessages[0].timestamp,
          referenceMode: 'before'
        });

      // loadMoreChatMessagesHandler can do stuff that breaks the scroll position
      if (this.scrolledToBottom) this.scrollToBottomOfMessages();
    },
    // emoji popup --------------------------------------------------------------
    toggleSelectEmojiPopup() {
      this.showEmojiPicker = !this.showEmojiPicker;
      this.lastOpenedEmojiPicker = Date.now();
      this.focusChatInput();
    },
    closeEmojiPopup() {
      if (this.showEmojiPicker && Date.now() > this.lastOpenedEmojiPicker + 400) {
        this.showEmojiPicker = false;
        this.focusChatInput();
      }
    },
    selectEmoji({ data }) {
      this.messageInputValue = `${this.messageInputValue}${data}`;
      this.focusChatInput();
      this.closeEmojiPopup();
      this.$nextTick(() => this.scrollToEndOfChatInput());
    },
    async load() {
      if (this.loaded) return console.warn('trying to load chat when already loaded');
      if (
        !this.getCurrentChatId ||
        (this['routing/routeName'] === 'room' &&
          (this.getCurrentChatId === this.getCurrentAreaId || this.getCurrentChatId === this.getCurrentAreaHubId))
      ) {
        console.warn('Trying to load chat with no chat id or hub id when on room page');
        this.chatBlocked = true;
        return;
      } else this.chatBlocked = false;

      this.setNewChatWaiting(false);

      await this.loadChatHandler({
        referenceMode: 'latest'
      });
      this.loaded = true;

      await this.$nextTick();

      this.scrollToBottomOfMessages(true);
      this.focusChatInput();
    },
    unload() {
      if (!this.loaded) return;
      this.loaded = false;
      this.clearCurrentReplyMessage();
      this.clearChatMessages();
    },
    customiseRoom() {
      this['modal/open']([CustomiseRoomModal]);
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.unload();
  }
};
</script>

<style lang="scss" scoped>
@import '~@/scss/components/sidebar/chat.scss';
</style>
